import React, { useState } from 'react';

import { GlobalAccountFeeRule, PricingPackage } from '@ndvr/billing-api';

import { useRequiredParams } from 'utils';
import { Text, VerticalLayout } from 'widgets';

import { useDeleteGlobalAccountFeeRule, useGlobalAccountFeeRuleTimeline, usePricingPackages } from './billing-queries';
import { PricingProfileDrawer } from './drawers/PricingProfileDrawer';
import { TimelinePanel } from './TimelinePanel';
import { PricingInfo } from './widgets/PricingInfo';
import { TimelineHeader } from './widgets/TimelineHeader';

export const PricingProfilePanel = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);

    const { data: timeline, isLoading } = useGlobalAccountFeeRuleTimeline(globalAccountGUID);
    const { data: pricingPackages = [] } = usePricingPackages();
    const { mutateAsync: deleteGlobalAccountFeeRule } = useDeleteGlobalAccountFeeRule(globalAccountGUID);

    const [editedRule, setEditedRule] = useState<GlobalAccountFeeRule>();
    const [drawerOpen, setDrawerOpen] = useState(false);

    return (
        <>
            <TimelineHeader
                title="Pricing"
                buttonLabel="+ New Pricing Profile"
                onClick={() => {
                    setEditedRule(undefined);
                    setDrawerOpen(true);
                }}
            />
            <TimelinePanel
                timeline={timeline}
                isLoading={isLoading}
                onEdit={(rule) => {
                    setEditedRule(rule);
                    setDrawerOpen(true);
                }}
                onDelete={(rule) => deleteGlobalAccountFeeRule(rule.globalAccountFeeRuleGUID)}
                firstColumnRenderer={({ rule }) => (
                    <VerticalLayout>
                        <Text largeBody>{getProfileTitle(rule, pricingPackages)}</Text>
                        <Text $themeColor="subtle">Household</Text>
                    </VerticalLayout>
                )}
                secondColumnRenderer={({ rule }) => (
                    <PricingInfo
                        pricingPackages={pricingPackages}
                        pricingPackageGUID={rule.pricingPackageGUID}
                        fixedFee={rule.fixedFee}
                        annualBps={rule.annualBps}
                    />
                )}
            />
            {drawerOpen && (
                <PricingProfileDrawer
                    globalAccountFeeRule={editedRule}
                    pricingPackages={pricingPackages}
                    onClose={() => setDrawerOpen(false)}
                />
            )}
        </>
    );
};

const getProfileTitle = (rule: GlobalAccountFeeRule, pricingPackages: PricingPackage[]): string => {
    if (!rule.pricingPackageGUID) {
        return 'Custom Profile';
    }
    const selectedPackage = pricingPackages.find(
        (pricingPackage) => pricingPackage.pricingPackageGUID === rule.pricingPackageGUID,
    );
    return selectedPackage?.pricingPackageName ?? 'Unknown';
};
